import {
  ACCOUNT_STATUSES,
  CUSTOMER_CONTRACT_ROLES,
  NEXT_PAYMENT_OPTIONS,
  PAYMENT_RESTRICTIONS,
  RECEIVER_TYPE
} from "../../../lib/constants";
import { CriteriaOption } from "../../../types/bulkEmails/CriteriaOption";
import { CriteriaRowModel } from "../../../types/bulkEmails/CriteriaRowModel";
import EXTERNAL_USER_CONTRACT_ROLES from "../../../types/enums/ExternalUserContractRole";
import { AccountNumberCriteriaSelect } from "../components/Criteria/AccountNumberCriteriaSelect";
import { DefaultCriteriaMultipleValueSelect } from "../components/Criteria/DefaultCriteriaMultipleValueSelect";
import { DefaultCurrencyRangeCriteria } from "../components/Criteria/DefaultCurrencyRangeCriteria";
import { DefaultDateRangeCriteria } from "../components/Criteria/DefaultDateRangeCriteria";
import { DefaultSwitchCriteria } from "../components/Criteria/DefaultSwitchCriteria";
import { ImpoundTypesCriteriaSelect } from "../components/Criteria/ImpoundTypesCriteriaSelect";
import { useMemo } from "react";

export const useCriteriaOptions = () => {
  const requiredRules = useMemo(() => [{ required: true, message: "Please select at least one option" }], []);

  const options: CriteriaOption[] = useMemo(
    () => [
      {
        key: "status",
        title: "Account Status",
        formKeys: [["criteria", "statusesFilter"]],
        render: (props) => (
          <DefaultCriteriaMultipleValueSelect {...props} options={ACCOUNT_STATUSES} rules={requiredRules} />
        )
      },
      {
        key: "role",
        title: "Role",
        formKeys: [["criteria", "recipientTypesFilter"]],
        render: (props) => (
          <DefaultCriteriaMultipleValueSelect {...props} options={CUSTOMER_CONTRACT_ROLES} rules={requiredRules} />
        )
      },
      {
        key: "accountNumber",
        title: "Account Number",
        formKeys: [["criteria", "contractNumbersFilter"]],
        render: (props) => <AccountNumberCriteriaSelect {...props} />
      },
      {
        key: "nextPayment",
        title: "Next Payment",
        formKeys: [["criteria", "nextPaymentDatesFilter"]],
        render: (props) => (
          <DefaultCriteriaMultipleValueSelect {...props} options={NEXT_PAYMENT_OPTIONS.filter((x) => x.value !== 0)} />
        )
      },
      {
        key: "impoundType",
        title: "Impound Type",
        formKeys: [["criteria", "impoundTypesFilter"]],
        render: (props) => <ImpoundTypesCriteriaSelect {...props} />
      },
      {
        key: "paymentRestrictions",
        title: "Payment Restrictions",
        formKeys: [["criteria", "paymentRestrictionsFilter"]],
        render: (props: any) => <DefaultCriteriaMultipleValueSelect {...props} options={PAYMENT_RESTRICTIONS} />
      },
      {
        key: "customerType",
        title: "Customer Type",
        formKeys: [["criteria", "customerTypeFilter"]],
        render: (props: any) => <DefaultCriteriaMultipleValueSelect {...props} options={RECEIVER_TYPE} />
      },
      {
        key: "autocharge",
        title: "Autocharge",
        formKeys: [["criteria", "autocharge"]],
        render: (props) => <DefaultSwitchCriteria {...props} />
      },
      {
        key: "nextAutochargeDateRange",
        title: "Next Autocharge Date",
        dependencies: { autocharge: true },
        formKeys: [
          ["criteria", "nextAutochargeDateFrom"],
          ["criteria", "nextAutochargeDateTo"]
        ],
        render: (props) => <DefaultDateRangeCriteria {...props} placeholders={["From:", "To:"]} />
      },
      {
        key: "lastPaymentDateRange",
        title: "Last Payment Date",
        formKeys: [
          ["criteria", "lastPaymentDateFrom"],
          ["criteria", "lastPaymentDateTo"]
        ],
        render: (props) => <DefaultDateRangeCriteria {...props} placeholders={["From:", "To:"]} />
      },
      {
        key: "lateChargesRange",
        title: "Late Charges",
        formKeys: [
          ["criteria", "unpaidLateChargesFrom"],
          ["criteria", "unpaidLateChargesTo"]
        ],
        render: (props) => <DefaultCurrencyRangeCriteria {...props} placeholders={["From:", "To:"]} />
      }
    ],
    [requiredRules]
  );

  const defaultCriteria: Omit<CriteriaRowModel, "id">[] = useMemo(
    () => [
      {
        optionKey: "status",
        value: [ACCOUNT_STATUSES.find((x) => x.label === "Open")?.value],
        removable: false
      },
      {
        optionKey: "role",
        value: [EXTERNAL_USER_CONTRACT_ROLES.PrimaryBuyer, EXTERNAL_USER_CONTRACT_ROLES.PrimarySeller],
        removable: false
      }
    ],
    []
  );

  return { options, defaultCriteria };
};
